<template>
  <div class="tv-layout">
    <!-- 顶部工具栏 -->
    <div class="tv-header">
      <div class="header-left">
        <button class="btn-back" @click="goToStrategy" title="返回策略管理">
          ← 返回
        </button>
        <div class="logo">📊</div>
        <div class="symbol-selector">
          <select v-model="currentFile" @change="handleSymbolChange">
            <option v-for="file in dataFiles" :key="file.filename" :value="file.filename">
              {{ file.symbol }}
            </option>
          </select>
        </div>
        <div class="period-selector">
          <select v-model="currentPeriod" @change="handlePeriodChange">
            <option value="1min">1分钟</option>
            <option value="5min">5分钟</option>
            <option value="15min">15分钟</option>
            <option value="30min">30分钟</option>
            <option value="1h">1小时</option>
            <option value="4h">4小时</option>
            <option value="1d" selected>日线</option>
            <option value="1w">周线</option>
            <option value="1M">月线</option>
          </select>
        </div>
      </div>

      <div class="header-right">
        <div class="header-info">
          <span class="info-item">{{ positions.length }} 持仓</span>
          <span class="info-item" :class="account.totalPnl >= 0 ? 'profit' : 'loss'">
            {{ formatPnl(account.totalPnl) }}
          </span>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="tv-main">
      <!-- 图表区 -->
      <div class="tv-chart">
        <KlineChart
          ref="klineChart"
          :data="chartData"
          :currentIndex="currentIndex"
          class="chart-full"
        />
        <PlayerControls
          :currentIndex="currentIndex"
          :totalBars="totalBars"
          :isPlaying="isPlaying"
          class="tv-player"
          @play="play"
          @pause="pause"
          @stop="stop"
          @reset="resetData"
          @prev="prevBar"
          @next="nextBar"
          @seek="seek"
        />
      </div>

      <!-- 右侧面板 -->
        <div class="tv-sidebar">
          <!-- 策略信息 -->
          <div class="sidebar-card strategy-info-card">
            <div class="info-row">
              <span class="info-label">策略</span>
              <span class="info-value strategy-name">{{ currentStrategyName || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">权益</span>
              <span class="info-value">¥{{ formatNumber(account.currentCapital || 0) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">盈亏</span>
              <span class="info-value" :class="account.totalPnl >= 0 ? 'profit' : 'loss'">
                {{ formatPnl(account.totalPnl) }}
              </span>
            </div>
          </div>

          <!-- 账户信息 -->
          <AccountCard :account="account" class="sidebar-card" />

          <!-- 持仓列表 -->
          <PositionCard
            :positions="positions"
            @close="closePosition"
            @closeAll="closeAllPositions"
            class="sidebar-card"
          />

          <!-- 交易面板切换按钮 -->
          <div class="sidebar-card trade-panel-card">
            <button 
              class="trade-panel-toggle" 
              @click="showTradePanel = !showTradePanel"
              :class="{ 'active': showTradePanel }"
            >
              📈 {{ showTradePanel ? '隐藏' : '显示' }}交易面板
            </button>
          </div>

        </div>
    </div>

    <!-- 浮动交易面板 -->
    <FloatingTradePanel 
      v-if="showTradePanel"
      :symbol="currentSymbol"
      @submit="placeOrder"
      @close="showTradePanel = false"
    />


  </div>
</template>

<script>
import KlineChart from '../components/KlineChart.vue'
import PlayerControls from '../components/PlayerControls.vue'
import AccountCard from '../components/AccountCard.vue'
import PositionCard from '../components/PositionCard.vue'
import FloatingTradePanel from '../components/FloatingTradePanel.vue'
import { quantApi } from '../api/quant'

export default {
  name: 'TradingApp',
  components: {
    KlineChart,
    PlayerControls,
    AccountCard,
    PositionCard,
    FloatingTradePanel
  },
  data() {
    return {
      userId: 1, // 默认用户ID，实际应该从登录状态获取
      chartData: [],
      currentIndex: 0,
      totalBars: 0,
      isPlaying: false,
      account: { initialCapital: 1000000, currentCapital: 1000000, totalPnl: 0 },
      positions: [],
      tradeHistory: [],
      showTradePanel: true,
      dataFiles: [],
      currentFile: '',
      currentPeriod: '1d',
      playInterval: null,
      saveProgressDebounce: null,
      saveSessionDebounce: null,
      updateStatusDebounce: null,
      updatePositionDebounce: null,
      currentStrategyName: '',
      currentStrategyId: null
    }
  },
  computed: {
    currentSymbol() {
      if (!this.currentFile) return 'JM'
      const parts = this.currentFile.replace('.parquet', '').split('_')
      if (parts.length >= 2) {
        const rawSymbol = parts[1]
        const { product } = quantApi.parseContractName(rawSymbol)
        return product || 'JM'
      }
      return 'JM'
    }
  },
  watch: {
    currentIndex() {
      this.debouncedSaveProgress()
      this.debouncedSaveSession()
      // 自动更新持仓价格（使用防抖）
      this.debouncedUpdatePositionPrice()
    },
    currentFile() {
      this.debouncedSaveSession()
    },
    currentPeriod() {
      this.debouncedSaveSession()
    },
    positions: {
      handler() {
        this.debouncedSaveSession()
      },
      deep: true
    },
    tradeHistory: {
      handler() {
        this.debouncedSaveSession()
      },
      deep: true
    }
  },
  mounted() {
    this.init()
    window.addEventListener('keydown', this.handleKeyDown)
  },
  beforeDestroy() {
    this.pause()
    window.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    goToStrategy() {
      this.$router.push('/strategy')
    },
    async updatePositionPriceFromKline() {
      // 如果有持仓且有K线数据，自动更新持仓价格
      if (this.positions && this.positions.length > 0 && this.chartData && this.chartData.length > 0) {
        const currentBar = this.chartData[this.currentIndex]
        if (currentBar && currentBar.close) {
          try {
            await quantApi.updatePositionPrice(currentBar.close)
            // 更新成功后，重新获取账户信息
            await this.updateStatus()
          } catch (error) {
          }
        }
      }
    },
    debouncedUpdatePositionPrice() {
      if (this.updatePositionDebounce) {
        clearTimeout(this.updatePositionDebounce)
      }
      this.updatePositionDebounce = setTimeout(() => {
        this.updatePositionPriceFromKline()
      }, 800)
    },
    debouncedSaveProgress() {
      if (this.saveProgressDebounce) {
        clearTimeout(this.saveProgressDebounce)
      }
      this.saveProgressDebounce = setTimeout(() => {
        this.saveProgress()
      }, 1000)
    },
    debouncedSaveSession() {
      if (this.saveSessionDebounce) {
        clearTimeout(this.saveSessionDebounce)
      }
      this.saveSessionDebounce = setTimeout(() => {
        this.saveSession()
      }, 1500)
    },
    debouncedUpdateStatus() {
      if (this.updateStatusDebounce) {
        clearTimeout(this.updateStatusDebounce)
      }
      this.updateStatusDebounce = setTimeout(() => {
        this.updateStatus()
      }, 600)
    },
    formatPnl(value) {
      if (value === null || value === undefined) return '¥0'
      const sign = value >= 0 ? '+' : ''
      return `${sign}¥${value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '0'
      return value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    async saveProgress(period = null) {
      if (!this.currentFile) return
      try {
        const response = await fetch('/api/quant/progress/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: this.currentFile,
            current_index: this.currentIndex,
            period: period || this.currentPeriod
          })
        })
        if (response.ok) {
          const result = await response.json()
        }
      } catch (error) {
      }
    },
    async restoreProgress() {
      if (!this.currentFile || this.chartData.length === 0) return
      try {
        const response = await fetch(`/api/quant/progress/${this.currentFile}/${this.currentPeriod}`)
        if (response.ok) {
          const result = await response.json()
          this.currentIndex = Math.min(result.current_index || 0, this.chartData.length - 1)
        }
      } catch (error) {
      }
    },
    async getProgressForFile(filename, period) {
      try {
        const response = await fetch(`/api/quant/progress/${filename}/${period}`)
        if (response.ok) {
          const result = await response.json()
          return result.current_index || 0
        }
      } catch (error) {
      }
      return 0
    },
    async loadFileList() {
      try {
        const result = await quantApi.getFileList()
        this.dataFiles = result.files || []
      } catch (error) {
      }
    },
    async saveSession() {
      try {
        const sessionData = {
          session_key: `strategy_${this.currentStrategyId || 'default'}`,
          current_file: this.currentFile,
          current_index: this.currentIndex,
          current_period: this.currentPeriod,
          current_strategy_id: this.currentStrategyId,
          trading_state: this.account,
          positions: this.positions,
          trade_history: this.tradeHistory
        }
        
        const response = await fetch('/api/quant/session/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sessionData)
        })
        if (response.ok) {
        }
      } catch (error) {
      }
    },
    async restoreSession() {
      try {
        // 获取URL中的策略ID
        const strategyIdFromUrl = this.$route.params.strategyId
        const sessionKey = strategyIdFromUrl ? `strategy_${strategyIdFromUrl}` : 'default_session'
        
        const response = await fetch(`/api/quant/session/${sessionKey}`)
        if (response.ok) {
          const result = await response.json()
          if (result.success && result.session) {
            const session = result.session
            
            if (session.current_period) {
              this.currentPeriod = session.current_period
            }
            
            await this.loadFileList()
            
            const sessionFileExists = session.current_file && this.dataFiles.some(file => file.filename === session.current_file)
            
            if (sessionFileExists && this.chartData.length === 0) {
              this.currentFile = session.current_file
              await this.handleSymbolChange()
            } else if (!sessionFileExists && this.dataFiles.length > 0) {
              this.currentFile = this.dataFiles[0].filename
              await this.handleSymbolChange()
            }
            
            if (session.trading_state) {
              this.account = session.trading_state
            }
            
            if (session.positions && session.positions.length > 0) {
              this.positions = session.positions
            }
            
            if (session.trade_history && session.trade_history.length > 0) {
              this.tradeHistory = session.trade_history
            }
            
            return true
          }
        }
        return false
      } catch (error) {
        return false
      }
    },
    async handleSymbolChange() {
      if (!this.currentFile) return
      try {
        await this.saveProgress()
        
        const result = await quantApi.getBars(this.currentFile, { 
          period: this.currentPeriod 
        })
        this.chartData = result.data || []
        this.totalBars = result.total || this.chartData.length

        const savedProgress = await this.getProgressForFile(this.currentFile, this.currentPeriod)
        this.currentIndex = Math.min(savedProgress, this.chartData.length - 1)

        if (this.$refs.klineChart) {
          this.$refs.klineChart.resetChart()
        }

        this.debouncedUpdateStatus()
      } catch (error) {
      }
    },
    async handlePeriodChange() {
      if (!this.currentFile) return
      try {
        let fromTime = null
        let toTime = null
        
        if (this.chartData && this.chartData.length > 0) {
          const timestamps = this.chartData.map(item => item.time)
          fromTime = Math.floor(Math.min(...timestamps) / 1000)
          toTime = Math.floor(Math.max(...timestamps) / 1000)
        }
        
        const result = await quantApi.getBars(this.currentFile, { 
          period: this.currentPeriod,
          fromTime,
          toTime
        })
        this.chartData = result.data || []
        this.totalBars = result.total || this.chartData.length
        
        const savedProgress = await this.getProgressForFile(this.currentFile)
        this.currentIndex = Math.min(savedProgress, this.chartData.length - 1)

        if (this.$refs.klineChart) {
          this.$refs.klineChart.resetChart()
        }

        this.debouncedUpdateStatus()
      } catch (error) {
      }
    },
    async loadData() {
      try {
        await this.loadFileList()

        const files = await quantApi.getFileList()
        if (files.files && files.files.length > 0) {
          const firstFile = files.files[0].filename
          const result = await quantApi.getDataFile(firstFile, { period: this.currentPeriod })
          this.chartData = result.data || []
          this.totalBars = result.total || this.chartData.length
          this.currentIndex = result.current_index || 0

          if (result.filename) {
            this.currentFile = result.filename
          }

          await new Promise(resolve => setTimeout(resolve, 0))
          
          if (this.$refs.klineChart) {
            this.$refs.klineChart.resetChart()
          }

          this.restoreProgress()
        }
      } catch (error) {
      }
    },
    async play() {
      if (this.isPlaying) return
      this.isPlaying = true

      this.playInterval = setInterval(async () => {
        if (this.currentIndex >= this.totalBars - 1) {
          this.pause()
          return
        }

        this.currentIndex++
        this.debouncedUpdateStatus()
      }, 500)
    },
    pause() {
      this.isPlaying = false
      if (this.playInterval) {
        clearInterval(this.playInterval)
        this.playInterval = null
      }
    },
    stop() {
      this.pause()
      this.currentIndex = 0
    },
    resetData() {
      this.stop()
      this.currentIndex = 0
    },
    handleKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.prevBar()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.nextBar()
      }
    },
    prevBar() {
      if (this.currentIndex > 0) {
        this.currentIndex--
        this.debouncedUpdateStatus()
      }
    },
    nextBar() {
      if (this.currentIndex < this.totalBars - 1) {
        this.currentIndex++
        this.debouncedUpdateStatus()
      }
    },
    seek(index) {
      this.currentIndex = index
      this.debouncedUpdateStatus()
    },
    async placeOrder(orderData) {
      try {
        const orderWithIndex = {
          ...orderData,
          currentIndex: this.currentIndex,
          price_per_tick: orderData.price_per_tick || 25
        }

        const result = await quantApi.placeOrder(orderWithIndex)

        if (result.success) {
          await this.updateStatus()
        } else if (result.message) {
          this.showOrderError(result.message)
        }
      } catch (error) {
        let errorMessage = '下单失败，请重试'
        if (error.response && error.response.data) {
          if (error.response.data.message) {
            errorMessage = error.response.data.message
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail
          }
        }
        this.showOrderError(errorMessage)
      }
    },
    showOrderError(message) {
      const errorDiv = document.createElement('div')
      errorDiv.style.cssText = `
        position: fixed;
        top: 20%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        padding: 20px 30px;
        color: #dc2626;
        font-size: 14px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        z-index: 9999;
        animation: fadeIn 0.3s ease;
      `
      errorDiv.textContent = message
      document.body.appendChild(errorDiv)
      
      setTimeout(() => {
        errorDiv.style.animation = 'fadeOut 0.3s ease'
        setTimeout(() => {
          document.body.removeChild(errorDiv)
        }, 300)
      }, 3000)
    },
    async closePosition(positionId) {
      try {
        if (!positionId || String(positionId).trim() === '') {
          return
        }
        const result = await quantApi.closePosition(positionId)
        await this.updateStatus()
      } catch (error) {
      }
    },
    async closeAllPositions() {
      try {
        const result = await quantApi.closeAllPositions()
        await this.updateStatus()
      } catch (error) {
      }
    },
    async runBacktest(params) {
      try {
        const result = await quantApi.backtest(params)
      } catch (error) {
      }
    },
    async updateStatus() {
      try {
        const result = await quantApi.getStatus(this.currentIndex)

        const newAccount = result.account || {}
        const totalCapital = newAccount.total_capital !== undefined
          ? newAccount.total_capital
          : newAccount.current_capital !== undefined
            ? newAccount.current_capital
            : newAccount.available_capital !== undefined && newAccount.frozen_capital !== undefined
              ? newAccount.available_capital + newAccount.frozen_capital
              : this.account.totalCapital !== undefined
                ? this.account.totalCapital
                : this.account.currentCapital

        this.account = {
          initialCapital: this.account.initialCapital || newAccount.initial_capital || 1000000,
          currentCapital: totalCapital,
          totalPnl: newAccount.total_pnl !== undefined ? newAccount.total_pnl : this.account.totalPnl,
          cumulativePnl: newAccount.cumulative_pnl !== undefined ? newAccount.cumulative_pnl : this.account.cumulativePnl || 0,
          frozenCapital: newAccount.frozen_capital !== undefined ? newAccount.frozen_capital : this.account.frozenCapital,
          totalCapital: totalCapital
        }

        this.positions = result.positions || []
      } catch (error) {
      }
    },
    async init() {
      // 先加载策略信息，确保 currentStrategyId 已设置
      await this.loadCurrentStrategy()
      
      // 通知后端切换到当前策略
      if (this.currentStrategyId) {
        try {
          await quantApi.selectStrategy(this.currentStrategyId)
        } catch (error) {
          console.error('切换策略失败:', error)
        }
      }
      
      // 再恢复会话（此时 session_key 会使用正确的策略ID）
      const sessionRestored = await this.restoreSession()
      
      if (!sessionRestored || (this.dataFiles.length === 0 && this.chartData.length === 0)) {
        await this.loadData()
      }
      
      await this.updateStatus()
    },
    async loadCurrentStrategy() {
      try {
        const result = await quantApi.getStrategies(this.userId)
        if (result.success && result.strategies && result.strategies.length > 0) {
          // 优先从URL参数获取策略ID
          const strategyIdFromUrl = this.$route.params.strategyId
          
          if (strategyIdFromUrl) {
            // 查找匹配的策略
            const strategy = result.strategies.find(s => s.id === parseInt(strategyIdFromUrl))
            if (strategy) {
              this.currentStrategyName = strategy.strategy_name || strategy.name
              this.currentStrategyId = strategy.id
              return
            }
          }
          
          // 如果URL中没有策略ID或未找到匹配策略，使用第一个策略
          const strategy = result.strategies[0]
          this.currentStrategyName = strategy.strategy_name || strategy.name
          this.currentStrategyId = strategy.id
        }
      } catch (error) {
      }
    },
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.tv-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #0a0e1a;
  color: #d1d4dc;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.tv-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #1e222d;
  border-bottom: 1px solid #2a2e39;
  height: 48px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-back {
  background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
  color: #e2e8f0;
  border: 1px solid #4a5568;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.btn-back:hover {
  background: linear-gradient(135deg, #5a6578 0%, #3d4858 100%);
  border-color: #5a6578;
  transform: translateX(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.btn-back:active {
  transform: translateX(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.logo {
  font-size: 20px;
  font-weight: 600;
}

.symbol-selector select,
.period-selector select {
  background: #2a2e39;
  color: #d1d4dc;
  border: 1px solid #363a45;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 13px;
  cursor: pointer;
}

.trade-panel-card {
  padding: 12px;
}

.trade-panel-toggle {
  width: 100%;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.trade-panel-toggle:hover {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
}

.trade-panel-toggle.active {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
}

.trade-panel-toggle.active:hover {
  background: linear-gradient(135deg, #06a370 0%, #059669 100%);
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.4);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 24px;
}

.header-info {
  display: flex;
  gap: 16px;
  font-size: 13px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.info-item.profit {
  color: #f23645;
}

.info-item.loss {
  color: #089981;
}

.tv-main {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.tv-chart {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

.chart-full {
  flex: 1;
  height: calc(100% - 40px);
}

.tv-player {
  height: 40px;
  border-top: 1px solid #2a2e39;
}

.tv-sidebar {
  width: 280px;
  background: #1e222d;
  border-left: 1px solid #2a2e39;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  overflow-y: auto;
}

.sidebar-card {
  background: #2a2e39;
  border-radius: 8px;
  padding: 12px;
}

.strategy-info-card {
  padding: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 12px;
  color: #848484;
  font-weight: 500;
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: #f9fafb;
}

.info-value.strategy-name {
  color: #60a5fa;
}

.info-value.profit {
  color: #f23645;
}

.info-value.loss {
  color: #089981;
}

.btn-tv-equity {
  width: 100%;
  background: #2962ff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-tv-equity:hover {
  background: #1e53e5;
}

.tv-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.tv-modal {
  background: #1e222d;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow: hidden;
}

.tv-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #2a2e39;
}

.tv-modal-title {
  font-size: 16px;
  font-weight: 600;
}

.tv-modal-close {
  background: transparent;
  border: none;
  color: #787b86;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tv-modal-close:hover {
  color: #d1d4dc;
}

.tv-modal-body {
  padding: 20px;
  height: calc(80vh - 60px);
  overflow: auto;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}
</style>